/**
 * PaneRoute component that renders content based on pane-specific routes
 */

import { usePaneRouterContext } from "@/context/PaneRouterContext";
import type { PaneRouteProps } from "@/types/pane-routing";
import React, { useMemo } from "react";
import { matchPath } from "react-router-dom";

/**
 * PaneRoute renders an element when the pane's location matches the specified path
 */
export const PaneRoute: React.FC<PaneRouteProps> = ({
  path,
  element,
  children,
  index = false,
}) => {
  const { location } = usePaneRouterContext();

  // Calculate if this route matches the current pane location
  const match = useMemo(() => {
    // Handle index routes
    if (index) {
      return location.pathname === "/"
        ? {
            params: {},
            pathname: "/",
            pathnameBase: "/",
            pattern: { path: "/", caseSensitive: false, end: true },
          }
        : null;
    }

    // Match the path against current location
    if (!path) return null;
    return matchPath({ path, end: true }, location.pathname || "");
  }, [path, location.pathname, index]);

  // If no match, don't render anything
  if (!match) {
    return null;
  }

  // Render the element or children
  return <>{element || children}</>;
};

/**
 * PaneRoutes component that renders the first matching PaneRoute
 */
export const PaneRoutes: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { location } = usePaneRouterContext();

  // Find the first matching route
  const matchingRoute = React.Children.toArray(children).find((child) => {
    if (!React.isValidElement(child) || child.type !== PaneRoute) {
      return false;
    }

    const props = child.props as PaneRouteProps;

    // Handle index routes
    if (props.index) {
      return location.pathname === "/";
    }

    // Check if path matches
    if (!props.path) return false;
    const match = matchPath(
      { path: props.path, end: true },
      location.pathname || "",
    );
    return match !== null;
  });

  return (matchingRoute as React.ReactElement) || null;
};
